// /events?e=UMSU-Welcome-Day — what an attendee opens from a poster QR or a
// shared link. The club and the event are named in the URL itself (see
// utils/eventSlug.js), so the link says what it opens before anyone taps it.
//
// The banner is the whole point: name, host logo, when, where, and a live
// countdown. It floats as an island over the map rather than sitting in a bar
// above it — the map is the page, and a full-width header with a hard edge cuts
// it in two on the screen where this is actually read, a phone held at the door.
//
// Three deliberate departures from the tool this was ported from:
//
//  · The visitor is asked for a PLACE, not a mode of travel. "How are you
//    getting here?" — walking / bus / driving / dropped off — was four buttons
//    that each stood in for a place the page then guessed at: the nearest bus
//    stop to the destination, the nearest of forty parking lots. The person
//    answering already knows which stop and which lot, because they are standing
//    at one, so the question is now the place itself and the answer comes out of
//    the same index the pathfinder searches — rooms, buildings, parking areas,
//    bike racks and bus stops are all things somebody can be standing at.
//    Somebody already on campus is not asked at all: their position is the
//    honest answer, and the quiet "change start location" line is there for the
//    one time it isn't.
//  · There is no "how are attendees arriving?" for the organiser at all — an
//    organiser knows what its event is, not where each attendee starts.
//  · Add to calendar, because an event map is used twice: once when the link
//    arrives, and once on the day. The first visit should be able to secure the
//    second.
//
// Routing is the pathfinder's own: PathfinderTab in driven mode (`driveRoute`,
// `hidePanel`), so the route line, floor switching and step directions are the
// same code students already use rather than a second implementation.
import { useState, useEffect, useMemo, useCallback } from 'react';
import PathfinderTab from './pathfinder/PathfinderTab.jsx';
import { createHttpDataSource } from './pathfinder/lib/dataSource.js';
import { haversineMeters } from './pathfinder/lib/geo.js';
import { buildingOriginNodeId, roomLabel, roomNumberBadge, searchBuildings, searchRooms }
  from './pathfinder/lib/graph.js';
import EventBody from './components/EventBody.jsx';
import { EventActions, EVENT_ACTIONS_CSS } from './components/EventActions.jsx';
import { ClubName, CLUB_SOCIALS_CSS } from './components/ClubSocials.jsx';
import { findEvent, eventUrl } from './utils/eventSlug.js';
import { qrBadgeSvg, qrBadgeSvgForDownload, MARK_CSS_WIDTH, UMAPS_BADGE_URL } from './utils/qrBadge.js';
import { eventStart, fmtWhen } from './utils/eventTime.js';
import { eventWhere, isLinkAddress, isOffCampus, offCampusAddress, offCampusLink }
  from './utils/eventTags.js';
import { EventTags, TicketButton, EVENT_TAGS_CSS } from './components/EventTags.jsx';

const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';
const MAPS_KEY = process.env.REACT_APP_MAPS_KEY;

// %20 — Safari's fetch() rejects a raw space (see PathfinderPage.js).
const snapshotSource = createHttpDataSource('/Map%20Info/pathfinder/', {
  imagesBase: process.env.REACT_APP_IMAGES_BASE || undefined,
});

const CAMPUS = { lat: 49.80939016935401, lng: -97.1343494222327 };
// Fort Garry is about 1 km across. Beyond 2 km somebody is travelling TO campus
// and where they will start from is a real question; inside it they are already
// here and the honest answer is "route me from where I'm standing".
const ON_CAMPUS_METERS = 2000;

// The snapshot's three non-buildings — the tunnel network, the outdoor graph and
// the inter-building links. Real entries in `mapData.buildings` because the
// router needs them, and nonsense as an answer to "where are you starting from".
const PSEUDO_BUILDINGS = new Set(['tunnels', 'outdoors', 'building_connections', 'parking']);

/** A pin, for the "where you are" row. */
function HereMark() {
  return (
    <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.4" />
    </svg>
  );
}

/** A live countdown, or what it turned into once the moment passed. */
function useCountdown(when) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick(n => n + 1), 1000 * 30);
    return () => clearInterval(t);
  }, []);
  if (!when) return { text: '', tone: '' };
  const ms = when.getTime() - Date.now();
  if (ms <= 0) return { text: 'Happening now', tone: 'now' };
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return { text: `In ${mins} min`, tone: 'soon' };
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return { text: `In ${hrs} hour${hrs === 1 ? '' : 's'}`, tone: 'soon' };
  const days = Math.floor(hrs / 24);
  return { text: `In ${days} day${days === 1 ? '' : 's'}`, tone: '' };
}

export default function EventsPage() {
  const [events, setEvents] = useState(null);
  const [err, setErr] = useState('');
  const [mapData, setMapData] = useState(null);
  const [pos, setPos] = useState(null);          // the visitor's position, or null
  const [posDone, setPosDone] = useState(false); // geolocation settled either way
  // Where the route starts: null until answered, then either { me: true } — the
  // visitor's own position — or a place picked out of the campus index, carrying
  // the node ids the router walks from and the name the route pill prints.
  const [from, setFrom] = useState(null);
  const [open, setOpen] = useState(false);       // the full event page
  const [qrOpen, setQrOpen] = useState(false);   // the shareable QR

  const wanted = useMemo(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return { slug: p.get('e') || '', id: p.get('id') || '' };
    } catch { return { slug: '', id: '' }; }
  }, []);

  // ⚠️ WHAT THE URL ASKED FOR IS SENT WITH THE REQUEST. An UNLISTED event is
  // deliberately absent from the public listing — that is what unlisted means —
  // and the server adds it back only for a request that already names it. Fetch
  // the bare list and the page would say "Event not found" for a link that is
  // supposed to keep working, which is exactly the poster-in-the-wrong-building
  // failure this whole feature exists to avoid.
  useEffect(() => {
    let dead = false;
    const qs = new URLSearchParams();
    if (wanted.slug) qs.set('e', wanted.slug);
    if (wanted.id) qs.set('id', wanted.id);
    const q = qs.toString();
    fetch(`${API_BASE}/api/events${q ? `?${q}` : ''}`)
      .then(r => r.json())
      .then(d => { if (!dead) setEvents(d.events || []); })
      .catch(() => { if (!dead) setErr('Could not load the event.'); });
    return () => { dead = true; };
  }, [wanted]);

  // One position fix. A refusal is an answer too — it settles `posDone`, which
  // is what makes the "where from" question appear rather than the page waiting
  // forever on a permission the visitor already declined.
  useEffect(() => {
    if (!navigator.geolocation) { setPosDone(true); return; }
    navigator.geolocation.getCurrentPosition(
      p => { setPos({ lat: p.coords.latitude, lng: p.coords.longitude }); setPosDone(true); },
      () => setPosDone(true),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
    );
  }, []);

  const event = useMemo(() => (events ? findEvent(events, wanted) : null), [events, wanted]);

  const onCampus = pos ? haversineMeters(pos, CAMPUS) <= ON_CAMPUS_METERS : false;

  // Somebody already on campus is never asked where they are starting from —
  // they are standing on the answer. Everyone else is asked once, including when
  // we could not get a position at all, since guessing wrong there sends them
  // the wrong way. "Change start location" is how either of them says otherwise.
  useEffect(() => {
    if (!posDone || from) return;
    if (onCampus) setFrom({ me: true, label: 'Where you are' });
  }, [posDone, onCampus, from]);

  const countdown = useCountdown(eventStart(event));

  /** The destination node(s): the event's room, or the building's entrance. */
  const toIds = useMemo(() => {
    if (!mapData || !event?.buildingSlug) return [];
    // roomIndex is an ARRAY of entries, not a Map — one entry per room, already
    // carrying every door of it in `nodeIds`.
    const want = String(event.room?.roomNumber || '').toLowerCase();
    const room = want
      ? (mapData.roomIndex || []).find(
          r => r.building === event.buildingSlug && String(r.roomNumber).toLowerCase() === want)
      : null;
    if (room?.nodeIds?.length) return room.nodeIds;
    if (room?.nodeId) return [room.nodeId];
    // ⚠️ A PINNED spot has no room number, so the lookup above never finds it.
    // Its stored `nodeIds` is the nav node it snapped to when it was placed,
    // and without this it would fall through to the building's exits — walking
    // people to the front door of a building whose atrium was the whole point.
    if (event.room?.nodeIds?.length) return event.room.nodeIds;
    // No room, or a room nobody has drawn: settle on the building's exits and
    // let the router pick the nearest one, which is what it does anyway.
    const exits = [];
    for (const n of mapData.nodes.values()) {
      if (n.building === event.buildingSlug && n.type === 'exit') exits.push(n.id);
    }
    return exits;
  }, [mapData, event]);

  /**
   * Where the route starts.
   *
   * A picked place already carries its own nodes — it came out of the routing
   * index, so it is a place the map can definitely walk from. "Where you are" is
   * the one that has to be resolved here: a position is not on the network, so
   * it snaps to the nearest node, and beyond a kilometre it snaps to nothing,
   * because a route drawn from the far side of the city is not a walk.
   */
  const fromIds = useMemo(() => {
    if (!mapData || !from) return [];
    if (!from.me) return from.ids || [];
    if (!pos) return [];
    let best = null, bestD = Infinity;
    for (const n of mapData.nodes.values()) {
      if (n.lat == null) continue;
      const d = haversineMeters(pos, n);
      if (d < bestD) { bestD = d; best = n.id; }
    }
    return best && bestD < 1000 ? [best] : [];
  }, [mapData, from, pos]);

  /**
   * What to call a node on the route pills.
   *
   * The generic "Start" / "End" the pathfinder falls back to are right when the
   * user typed the endpoints — they can already see what they asked for in the
   * search boxes. Here nobody typed anything: the page chose both ends on their
   * behalf, so the pills are the only place that says which of forty parking
   * lots it picked, and "Start" is precisely the word that withholds it.
   */
  const nodeName = useCallback((id) => {
    const n = mapData?.nodes?.get(id);
    if (!n) return '';
    if (n.label) return n.label;                     // parking lots, bus stops
    const b = (mapData.buildings || []).find(x => x.slug === n.building);
    const where = b?.name || '';
    const room = n.room_number || '';
    if (where && room) return `${where} ${room}`;
    return where || room || '';
  }, [mapData]);

  const startName = useMemo(
    () => from?.label || nodeName(fromIds[0]) || 'Start',
    [from, fromIds, nodeName]);

  const endName = useMemo(() => {
    const room = event?.room?.roomNumber;
    const b = event?.buildingName;
    if (b && room) return `${b} ${room}`;
    return b || nodeName(toIds[0]) || 'End';
  }, [event, toIds, nodeName]);

  if (!MAPS_KEY) return <Shell><div className="ev-msg">Missing <code>REACT_APP_MAPS_KEY</code>.</div></Shell>;
  if (err) return <Shell><div className="ev-msg">{err}</div></Shell>;
  if (events === null) return <Shell><div className="ev-msg">Loading…</div></Shell>;

  if (!event) {
    return (
      <Shell>
        <div className="ev-msg">
          <h1>Event not found</h1>
          <p>That link may have expired, or the event was taken down.</p>
          {/* The campus map, which is where "what's on" lives now — /eventmap is
              retired (see src/index.js, which still answers its old QR links). */}
          <a className="ev-btn" href="/umanitoba">See what’s on</a>
        </div>
      </Shell>
    );
  }

  const where = eventWhere(event);
  // ⚠️ An OFF-CAMPUS event turns this page into a different page. There is no
  // route to draw, so there is nothing to ask "where are you starting from"
  // for, and the answer to "how do I get there" is Google Maps rather than the
  // campus router. Everything else — the banner, the countdown, the club's
  // page, the QR — is unchanged, which is the point: the poster still works.
  const offsite = isOffCampus(event) ? offCampusLink(offCampusAddress(event)) : '';

  return (
    <Shell>
      <div className="ev-map">
        {/* The banner floats over the map as an island. It is the thing the
            attendee came for and the thing they re-open on the day, so it stays
            on screen — but the map underneath runs full-bleed behind it rather
            than starting below a hard edge, which on a phone is most of the
            difference between "a map with a title" and "a poster with a map
            stuck under it". */}
        <div className={`ev-island ${open ? 'open' : ''}`}>
        <header className="ev-banner" onClick={() => setOpen(v => !v)}>
          {event.logo && <img className="ev-logo" src={event.logo} alt="" />}
          <div className="ev-bmain">
            <h1>{event.name}</h1>
            <div className="ev-bmeta">
              {event.host && <ClubName name={event.host} socials={event.socials} room={event.clubRoom} />}
              <span>{fmtWhen(event)}</span>
              {where && <span>{where}</span>}
            </div>
          </div>
          <div className="ev-bright">
            {countdown.text && <span className={`ev-count ${countdown.tone}`}>{countdown.text}</span>}
            {/* A chevron, not the word "Details". The whole banner is the
                control; this only has to say that there is more underneath it,
                and a labelled button next to a tappable card invites the reader
                to work out which of the two they are supposed to press. */}
            <button className="ev-info" aria-label="Event details"
                    onClick={e => { e.stopPropagation(); setOpen(v => !v); }}>
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path d="M6 9.5 L12 15.5 L18 9.5" fill="none" stroke="currentColor"
                      strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </header>

        {/* The details are part of the same card, unrolling from underneath the
            header rather than arriving as a separate sheet — so the header IS
            the top of the details page, which is what the chevron on it implies. */}
        {open && (
          <Details event={event} where={where} onClose={() => setOpen(false)} />
        )}
        </div>

        {/* Driven mode: this page supplies the endpoints and the pathfinder
            renders the route, the floor switching and the step directions. */}
        <PathfinderTab
          apiKey={MAPS_KEY}
          dataSource={snapshotSource}
          hidePanel
          enableFeedback={false}
          onMapData={setMapData}
          driveRoute={fromIds.length && toIds.length ? { fromId: fromIds, toId: toIds } : null}
          driveFromLabel={startName}
          driveToLabel={endName}
        />
        {/* Off campus: the one thing this page can usefully do is hand the
            address over to something that CAN route to it. A card in the same
            bottom island the start-location question uses, because it answers
            the same question — this is just the answer for an event the campus
            map does not cover. */}
        {offsite && (
          <div className="ev-ask">
            <div className="ev-askhead">
              <strong>This one is off campus.</strong>
              <span>
                {isLinkAddress(offCampusAddress(event))
                  ? 'We can’t walk you there, but the club’s map link will.'
                  : offCampusAddress(event) || 'The club hasn’t added an address yet.'}
              </span>
            </div>
            {offCampusAddress(event) && (
              <a className="ev-btn" href={offsite} target="_blank" rel="noopener noreferrer">
                Open in Google Maps ↗
              </a>
            )}
          </div>
        )}
        {!offsite && !from && posDone && (
          /* An island at the bottom, matching the banner at the top: the map is
             the page, and both of these sit on it rather than boxing it in. The
             bottom is also where a thumb already is on the screen this is read
             on. */
          <div className="ev-ask">
            <div className="ev-askhead">
              <strong>Where are you starting from?</strong>
              <span>We’ll walk you from there to {where || event.buildingName || 'the event'}.</span>
            </div>
            <FromSearch mapData={mapData} pos={pos} onPick={setFrom} />
          </div>
        )}
        {/* Not shown for an off-campus event: nothing is missing there, and
            "no mapped destination" would read as a fault in an event that is
            exactly as complete as it is meant to be. */}
        {!offsite && from && !toIds.length && mapData && (
          <div className="ev-strip">
            This event has no mapped destination yet, so we can’t draw a route to it.
          </div>
        )}

        {/* The link, as a thing to point a phone at. An event map is passed on in
            person as often as it is sent — somebody at a table shows their screen
            to the person in front of them — and that is the whole job of this
            button.

            It waits while the start is being asked for: it sits in the same
            bottom-right corner the results list grows into, and a button
            floating over a list is a button somebody taps by accident. */}
        {from && !offsite && (
        <button className="ev-qrbtn" onClick={() => setQrOpen(true)} title="Show a QR code for this event">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
               strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="3" width="7" height="7" rx="1.2" />
            <rect x="14" y="3" width="7" height="7" rx="1.2" />
            <rect x="3" y="14" width="7" height="7" rx="1.2" />
            <path d="M14 14h3v3h-3zM20 14v3M14 20h3M20 20h1" />
          </svg>
          <span>QR code</span>
        </button>
        )}

        {qrOpen && <QrPopup event={event} all={events} onClose={() => setQrOpen(false)} />}

        {/* Changing where you started is a rare second thought, so it is a quiet
            line at the bottom rather than a button competing with the map. It is
            also the only way back for somebody the page answered on their behalf
            — standing on campus, routed from their own position — so it says
            what it changes rather than "start over". */}
        {from && !offsite && (
          <button className="ev-restart" onClick={() => setFrom(null)}>
            Change start location
          </button>
        )}
      </div>

    </Shell>
  );
}

/**
 * The "from" box: one field, answered against the campus index.
 *
 * Everything in `roomIndex` is somewhere a person can be standing — a room, a
 * parking area, a bike rack, a bus stop — and every entry already carries the
 * nodes the router walks from, so a place picked here is one the map can
 * definitely route out of. Buildings are searched alongside it because "I'm at
 * University Centre" is a whole answer and the room index cannot give it: a
 * building is not one of its rooms, and its own answer is its nearest entrance.
 *
 * Buildings are listed FIRST, capped at three. They are the coarse answer, so
 * they are what somebody typing a name rather than a number means — and behind
 * ten room hits they would be off the bottom of a box that is deliberately short.
 */
function FromSearch({ mapData, pos, onPick }) {
  const [q, setQ] = useState('');

  const options = useMemo(() => {
    if (!mapData || !q.trim()) return [];
    const out = [];
    const buildings = (mapData.buildings || []).filter(b => b.name && !PSEUDO_BUILDINGS.has(b.slug));
    for (const b of searchBuildings(buildings, q, 3)) {
      const id = buildingOriginNodeId(mapData, b.slug);
      out.push({ key: `b:${b.slug}`, label: b.name, sub: 'Building entrance', ids: id ? [id] : [] });
    }
    for (const r of searchRooms(mapData.roomIndex || [], q, 10)) {
      out.push({
        // ⚠️ The index, not the building and room. Everything outdoors — every
        // bus stop, every parking area — is filed under the `outdoors` building
        // with NO room number, so a key built from those two collides across all
        // of them: React saw one row repeated and warned about duplicate keys.
        key: `r:${out.length}:${r.building}:${r.roomNumber}`,
        label: roomLabel(r),
        sub: roomNumberBadge(r) || (r.busStop ? 'Bus stop' : ''),
        // A row the router cannot leave from — a lot with no entrance node, a
        // room whose doors were never linked — is shown and disabled rather than
        // hidden: it is a real place, and a search that has never heard of it
        // reads as broken. Same rule the pathfinder's own results follow.
        ids: r.nodeIds?.length ? r.nodeIds : (r.nodeId ? [r.nodeId] : []),
        off: r.connected === false,
      });
    }
    return out;
  }, [mapData, q]);

  return (
    <div className="ev-from">
      <input
        className="ev-input" value={q} onChange={e => setQ(e.target.value)}
        placeholder={mapData ? 'Room, building, parking lot or bus stop' : 'Loading campus…'}
        disabled={!mapData}
        // Enter with one obvious answer picks it, so the box can be finished
        // from a phone keyboard without reaching for the list.
        onKeyDown={e => {
          if (e.key !== 'Enter') return;
          const first = options.find(o => !o.off && o.ids.length);
          if (first) onPick({ label: first.label, ids: first.ids });
        }}
      />
      {/* Offered before any typing, because for most people it is the answer —
          and it is the one thing they cannot type. It disappears once they are
          typing: at that point they are answering the question themselves. */}
      {pos && !q.trim() && (
        <button className="ev-here" onClick={() => onPick({ me: true, label: 'Where you are' })}>
          <span className="ev-hicon"><HereMark /></span>
          <span>Where you are now</span>
        </button>
      )}
      {!!options.length && (
        <ul className="ev-fresults">
          {options.map(o => (
            <li key={o.key}>
              <button disabled={o.off || !o.ids.length}
                      onClick={() => onPick({ label: o.label, ids: o.ids })}>
                <span className="ev-flabel">{o.label}</span>
                <span className="ev-fsub">{o.off || !o.ids.length ? 'no route' : o.sub}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {mapData && !!q.trim() && !options.length && (
        <div className="ev-fnone">Nothing matches that. Try a room number, or the building's name.</div>
      )}
    </div>
  );
}

/**
 * The rest of the event page, unrolling from under the header.
 *
 * Not a separate sheet: it is the same card the banner is in, growing downward,
 * so the header the reader tapped stays put and becomes the top of what opens.
 * A sheet that slides over the top would cover the thing that opened it.
 */
function Details({ event, where, onClose }) {
  // Escape closes, like every other layer on the site.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="ev-details" onClick={e => e.stopPropagation()}>
      {/* The countdown leads. Somebody who has opened this card on the day is
          asking one question — how long have I got — and the answer should not
          be under a title they have already read twice on the way in. */}
      <EventActions event={event} />

      {/* The name again, in full, with the club on the same line.
          The header above is a compressed summary that has to fit one line and
          gets ellipsed; this is the page's own title, where it has the width to
          be read. "by UMSU" belongs BESIDE it rather than under it: a title and
          its author are one line of a poster, and stacking them spent a whole
          row on four small grey words. */}
      <div className="ev-dhead">
        <h2 className="ev-dtitle">{event.name}</h2>
        {event.host && (
          <span className="ev-dhost">
            by <ClubName name={event.host} socials={event.socials} room={event.clubRoom} />
          </span>
        )}
      </div>

      {where && <div className="ev-dwhere">{where}</div>}
      {/* Under the location, because "Engineering students only" is a fact
          about whether to set off at all and belongs beside where and when
          rather than buried under whatever the club wrote. */}
      <EventTags event={event} />
      <TicketButton event={event} />
      <EventBody blocks={event.blocks} />
    </div>
  );
}

/**
 * The event's link as a QR, with a download.
 *
 * SVG rather than a PNG: a club prints these onto posters, and a scaled-up
 * raster QR is a fuzzy one, which is an unscannable one. The link it encodes is
 * the shareable /events?e=Club-Event-Name form rather than window.location,
 * which may carry whatever query the current visitor arrived with.
 */
function QrPopup({ event, all, onClose }) {
  const url = eventUrl(event, undefined, all);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ⚠️ The mark is EMBEDDED in the downloaded file, not linked: this SVG
  // leaves the site, and a "/UMaps%20Logo…" reference resolves to nothing in
  // Illustrator, in Canva or at a print shop.
  const download = async () => {
    const blob = new Blob([await qrBadgeSvgForDownload(url, 1000)], { type: 'image/svg+xml' });
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = `${(event.name || 'event').replace(/[^A-Za-z0-9]+/g, '-')}-qr.svg`;
    a.click();
    URL.revokeObjectURL(href);
  };

  return (
    <div className="ev-qrscrim" onClick={onClose}>
      <div className="ev-qrbox" onClick={e => e.stopPropagation()} role="dialog" aria-label="Event QR code">
        <button className="ev-qrx" onClick={onClose} aria-label="Close">✕</button>
        <h3>{event.name}</h3>
        {/* Same treatment as the Event Map Creator and the printed building
            signs: the white disc is inside the SVG, the mark sits over it. */}
        <div className="ev-qrimg">
          {/* ⚠️ One wrapper around both — same fix as the creator's share step.
              The badge's containing block is `.ev-qrimg > div`, so the mark has
              to be INSIDE that div rather than a sibling of it; as a sibling it
              centred on the popup instead of on the code and sat low and off to
              one side. */}
          <div>
            <div dangerouslySetInnerHTML={{ __html: qrBadgeSvg(url, 420) }} />
            <img className="ev-qrbadge" src={UMAPS_BADGE_URL} alt=""
                 onError={e => { e.target.style.display = 'none'; }} />
          </div>
        </div>
        <div className="ev-qrurl">{url.replace(/^https?:\/\//, '')}</div>
        <button className="ea-btn" onClick={download}>Download QR code</button>
      </div>
    </div>
  );
}

function Shell({ children }) {
  return <div className="ev-root"><style>{CSS}</style>{children}</div>;
}

const CSS = EVENT_ACTIONS_CSS + CLUB_SOCIALS_CSS + EVENT_TAGS_CSS + `
.ev-root { --line:#e2e5ea; --dim:#6b7280; --accent:#4f46e5;
  position:fixed; inset:0; display:flex; flex-direction:column; background:#fff; color:#15181d;
  font:15px/1.5 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
.ev-msg { margin:auto; text-align:center; padding:28px; color:var(--dim); }
.ev-msg h1 { font-size:20px; color:#15181d; margin:0 0 6px; }

/* The floating island: the header, and everything that unrolls from under it.
   Insets rather than a full-width bar, so the map reads as one surface running
   underneath. The width is capped and centred because on a desktop a banner
   stretched across 1600px of satellite imagery stops looking like a card and
   starts looking like a broken header. */
.ev-island { position:absolute; z-index:6; top:10px; left:10px; right:10px;
  width:auto; max-width:640px; margin:0 auto;
  background:rgba(255,255,255,.97); -webkit-backdrop-filter:blur(10px); backdrop-filter:blur(10px);
  border:1px solid rgba(15,18,23,.07); border-radius:16px;
  box-shadow:0 6px 24px rgba(15,18,23,.16);
  display:flex; flex-direction:column; overflow:hidden;
  transition:box-shadow .18s ease; }
.ev-island.open { box-shadow:0 16px 44px rgba(15,18,23,.3);
  /* Never taller than the screen it is on: the body scrolls inside. */
  max-height:calc(100% - 20px); }
/* Clear of the notch / status bar when this is saved to a home screen. */
@supports (padding: max(0px)) {
  .ev-island { top:max(10px, env(safe-area-inset-top)); }
}

.ev-banner { flex:none; display:flex; align-items:center; gap:12px; padding:11px 14px;
  cursor:pointer; transition:background .15s ease; }
.ev-banner:hover { background:rgba(15,18,23,.03); }
/* The chevron turns over when the card is open — the one bit of state the header
   has to carry, since it is now the lid of the thing it opens. */
.ev-info svg { transition:transform .22s cubic-bezier(.22,1,.36,1); }
.ev-island.open .ev-info svg { transform:rotate(180deg); }

/* The body: it unrolls, and scrolls inside the card rather than growing past
   the bottom of the screen. */
.ev-details { flex:1; min-height:0; overflow:auto; padding:0 14px 14px;
  border-top:1px solid #d6d9de;
  display:flex; flex-direction:column; gap:14px;
  animation:ev-unroll .26s cubic-bezier(.22,1,.36,1); }
@keyframes ev-unroll { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:none; } }
.ev-details > :first-child { margin-top:14px; }
/* Title and club on one line, aligned on their baselines so "by UMSU" sits on
   the name rather than floating beside the middle of it. It wraps to two lines
   on a narrow phone with a long event name, which is the right failure. */
.ev-dhead { display:flex; align-items:baseline; flex-wrap:wrap; gap:2px 10px; }
.ev-dtitle { margin:0; font-size:20px; font-weight:800; line-height:1.25; }
.ev-dhost { font-size:13.5px; color:var(--dim); }
.ev-dwhere { font-size:13px; color:var(--dim); }


.ev-logo { width:44px; height:44px; object-fit:contain; border-radius:10px; flex:none; }
.ev-bmain { flex:1; min-width:0; }
.ev-bmain h1 { margin:0; font-size:17px; font-weight:800; overflow:hidden;
  text-overflow:ellipsis; white-space:nowrap; }
.ev-bmeta { display:flex; flex-wrap:wrap; gap:4px 10px; font-size:12.5px; color:var(--dim); }
.ev-bright { display:flex; flex-direction:column; align-items:flex-end; gap:5px; flex:none; }
.ev-count { font-size:12px; font-weight:800; color:var(--dim); }
.ev-count.soon { color:#b45309; }
.ev-count.now { color:#15803d; }
.ev-info { border:none; background:#f1f2f6; color:#42464e; border-radius:50%;
  width:26px; height:26px; display:grid; place-items:center; cursor:pointer;
  transition:background .15s ease; }
.ev-banner:hover .ev-info { background:#e6e8ee; }

.ev-map { flex:1; min-height:0; position:relative; }

/* The bottom island. Mirrors the banner's inset, radius and shadow so the two
   read as the same kind of object sitting on the same map. */
.ev-ask { position:absolute; z-index:5; left:10px; right:10px; bottom:10px;
  width:auto; max-width:640px; margin:0 auto;
  background:rgba(255,255,255,.97); -webkit-backdrop-filter:blur(10px); backdrop-filter:blur(10px);
  border:1px solid rgba(15,18,23,.07); border-radius:16px; padding:14px;
  box-shadow:0 6px 24px rgba(15,18,23,.18);
  animation:ev-rise .22s cubic-bezier(.22,1,.36,1); }
@supports (padding: max(0px)) {
  .ev-ask { bottom:max(10px, env(safe-area-inset-bottom)); }
}
@keyframes ev-rise { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:none; } }

/* Bottom right, opposite the quiet "change start location" line, so the two
   never sit on top of each other. */
.ev-qrbtn { position:absolute; z-index:5; right:12px; bottom:12px;
  display:flex; align-items:center; gap:7px;
  background:rgba(255,255,255,.96); -webkit-backdrop-filter:blur(8px); backdrop-filter:blur(8px);
  border:1px solid rgba(15,18,23,.08); color:#15181d; border-radius:999px;
  padding:9px 14px; font:inherit; font-size:13.5px; font-weight:700; cursor:pointer;
  box-shadow:0 4px 16px rgba(15,18,23,.2); }
.ev-qrbtn:hover { box-shadow:0 7px 22px rgba(15,18,23,.26); }
@supports (padding: max(0px)) {
  .ev-qrbtn { bottom:max(12px, env(safe-area-inset-bottom)); }
}
.ev-qrscrim { position:fixed; inset:0; z-index:110; background:rgba(15,18,23,.55);
  display:flex; align-items:center; justify-content:center; padding:16px; }
.ev-qrbox { position:relative; background:#fff; width:min(340px,100%); border-radius:18px;
  padding:22px; text-align:center; box-shadow:0 18px 50px rgba(15,18,23,.34);
  display:flex; flex-direction:column; gap:12px; align-items:center; }
.ev-qrbox h3 { margin:0 26px; font-size:16.5px; font-weight:800; }
.ev-qrx { position:absolute; top:12px; right:12px; border:none; background:#f1f2f6;
  width:28px; height:28px; border-radius:50%; font-size:13px; cursor:pointer; }
.ev-qrimg { width:100%; max-width:260px; line-height:0; }
.ev-qrimg > div { position:relative; }
.ev-qrimg svg { width:100%; height:auto; display:block; }
/* The width is MARK_CSS_WIDTH from utils/qrBadge.js, so this code, the printed
   signs and the app's share sheet carry the same mark at the same size — a
   number typed here is how those three stop matching. height:auto, never a
   fixed height beside a percentage width, or it renders as an oval. */
.ev-qrbadge { position:absolute; left:50%; top:50%; transform:translate(-50%,-50%);
  width:${MARK_CSS_WIDTH}; height:auto; display:block; }
.ev-qrurl { font-size:12px; color:var(--dim); word-break:break-all; }

/* Quiet on purpose: a second thought, not an action the page is asking for. */
.ev-restart { position:absolute; z-index:5; left:50%; transform:translateX(-50%); bottom:12px;
  border:none; background:rgba(255,255,255,.9); -webkit-backdrop-filter:blur(8px);
  backdrop-filter:blur(8px); color:var(--dim); border-radius:999px; padding:6px 14px;
  font:inherit; font-size:12.5px; cursor:pointer; box-shadow:0 2px 10px rgba(15,18,23,.14); }
.ev-restart:hover { color:#15181d; }
@supports (padding: max(0px)) {
  .ev-restart { bottom:max(12px, env(safe-area-inset-bottom)); }
}
.ev-askhead { display:flex; flex-direction:column; margin-bottom:10px; }
.ev-askhead span { font-size:12.5px; color:var(--dim); }

/* The "from" box. The results list is short on purpose: this island sits over
   the map showing the event, and a list that grows to ten rows covers the thing
   the reader came for. Six rows and a scroll is the trade. */
.ev-from { display:flex; flex-direction:column; gap:8px; }
/* 16px exactly — anything smaller makes iOS Safari zoom the page on focus and
   leaves the reader scrolled sideways over the map. */
.ev-input { width:100%; box-sizing:border-box; background:#fff; color:#15181d;
  border:1.5px solid var(--line); border-radius:11px; padding:10px 12px;
  font:inherit; font-size:16px; }
.ev-input:focus { outline:none; border-color:var(--accent); }
.ev-here { display:flex; align-items:center; gap:9px; width:100%; text-align:left;
  background:#f7f7ff; border:1.5px dashed #c7c9f5; border-radius:11px; padding:9px 12px;
  font:inherit; font-size:13.5px; font-weight:700; color:var(--accent); cursor:pointer; }
.ev-here:hover { border-style:solid; }
.ev-hicon { display:flex; }
.ev-fresults { list-style:none; margin:0; padding:4px; max-height:min(230px, 34vh); overflow:auto;
  border:1px solid var(--line); border-radius:11px; background:#fff; }
.ev-fresults button { display:flex; align-items:baseline; gap:10px; width:100%; text-align:left;
  background:none; border:none; border-radius:8px; padding:8px 10px; font:inherit;
  color:#15181d; cursor:pointer; }
.ev-fresults button:hover:not(:disabled) { background:#f2f2fd; }
.ev-fresults button:disabled { opacity:.5; cursor:default; }
.ev-flabel { font-size:14px; }
.ev-fsub { font-size:12px; color:var(--dim); margin-left:auto; white-space:nowrap; }
.ev-fnone { font-size:13px; color:var(--dim); }

.ev-strip { position:absolute; left:12px; right:12px; bottom:12px; z-index:5; background:#fef3c7;
  color:#92400e; border-radius:10px; padding:10px 12px; font-size:13px; }

.ev-btn { background:var(--accent); color:#fff; border:none; border-radius:10px; padding:10px 16px;
  font:inherit; font-size:14px; font-weight:700; cursor:pointer; text-decoration:none;
  display:inline-block; }
.ev-btn:disabled { opacity:.5; cursor:default; }
.ev-btn.ghost { background:#fff; color:#15181d; border:1.5px solid var(--line); }

@media (prefers-reduced-motion: reduce) { .ev-details { animation:none; } }
`;
